import { useState } from 'react'
import { useCampaignRole } from '../../hooks/useCampaignRole'

interface BiographyEntry {
  id: string
  event_label: string
  flavour_text: string | null
  character_age: number
  game_year: number | null
  is_gm_only: boolean
  effects_applied: Record<string, unknown>
}

interface Props {
  character: {
    biography?: BiographyEntry[]
    narrative?: string[]
  }
}

export function HistoryPanel({ character }: Props) {
  const { isGM } = useCampaignRole()
  const [view, setView] = useState<'narrative' | 'events'>('narrative')
  const allEntries = character.biography ?? []
  const entries = isGM ? allEntries : allEntries.filter(e => !e.is_gm_only)
  const narrative = character.narrative ?? []

  if (entries.length === 0 && narrative.length === 0) {
    return (
      <div className="border border-parchment-dark rounded p-4 mb-4 bg-white">
        <h3 className="font-cinzel font-semibold text-sm text-accent uppercase tracking-wider mb-3 pb-2 border-b-2 border-accent/20">Life History</h3>
        <p className="text-sm text-ink-light">No history recorded.</p>
      </div>
    )
  }

  const sorted = [...entries].sort((a, b) => a.character_age - b.character_age)

  return (
    <div className="border border-parchment-dark rounded p-4 mb-4 bg-white">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-cinzel font-semibold text-sm text-accent uppercase tracking-wider mb-3 pb-2 border-b-2 border-accent/20">Life History ({entries.length} events)</h3>
        {narrative.length > 0 && (
          <div className="flex gap-1">
            <button onClick={() => setView('narrative')}
              className={`text-xs px-2 py-0.5 rounded ${view === 'narrative' ? 'bg-accent text-white font-bold' : 'border border-parchment-dark hover:bg-parchment-dark'}`}>
              Narrative
            </button>
            <button onClick={() => setView('events')}
              className={`text-xs px-2 py-0.5 rounded ${view === 'events' ? 'bg-accent text-white font-bold' : 'border border-parchment-dark hover:bg-parchment-dark'}`}>
              Events
            </button>
          </div>
        )}
      </div>

      {view === 'narrative' && narrative.length > 0 ? (
        <div className="prose prose-sm max-h-96 overflow-y-auto text-sm space-y-1 text-ink-light leading-relaxed"
          dangerouslySetInnerHTML={{ __html: narrative.map(line =>
            line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
              .replace(/\*(.+?)\*/g, '<em>$1</em>')
          ).join('<br/>') }}
        />
      ) : (
        <div className="space-y-1 max-h-96 overflow-y-auto">
          {sorted.map(e => (
            <div key={e.id} className="flex gap-2 text-sm py-0.5 border-b border-parchment/30">
              <div className="flex-shrink-0 w-20 text-right">
                <span className="font-semibold">Age {e.character_age}</span>
                {e.game_year && <span className="text-xs text-ink-light block">{e.game_year} TR</span>}
              </div>
              <div className="flex-1">
                <span className="font-medium">{e.event_label}</span>
                {e.flavour_text && (
                  <span className="text-ink-light ml-1">— {e.flavour_text}</span>
                )}
                {e.is_gm_only && (
                  <span className="ml-1 px-1 py-0.5 bg-warning text-white text-xs rounded">GM</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
